import { c as _c } from "react/compiler-runtime";
import AlertComponent, { type Props as AlertProps } from '../alert/alert.component';
import ButtonComponent, { IconSize } from '../button/button/button.component';
import { type ComponentRef, ReactElement, ReactNode, forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { ButtonType } from '../button/types/ButtonType';
import ImageComponent from '@spa/components/image-component/image.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import styles from './popup.component.module.scss';
import useBodyOverflowHidden from '../../lib/react/hooks/useBodyOverflowHidden';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
export enum PopupType {
  DEFAULT,
  SUCCESS,
  FULLSCREEN,
  FULLSCREEN_MOBILE,
  STOREFINDER,
  CENTER,
  LEGAL_NOTICE,
}
export enum PopupContext {
  DEFAULT = 'DEFAULT',
  WISHLIST = 'WISHLIST',
  WITHOUT_TITLE = 'WITHOUT_TITLE',
}
export interface Props {
  readonly alertConfig?: AlertProps;
  readonly focusDialogOnOpen?: boolean;
  readonly closable?: boolean;
  readonly children: ReactNode;
  readonly footerContent?: ReactNode;
  readonly isOpen: boolean;
  readonly onClosed: () => void;
  readonly popupType: PopupType;
  readonly popupContext?: PopupContext;
  readonly dialogClassName?: string;
  readonly overlayClassName?: string;
  readonly headerClassName?: string;
  readonly title?: string;
  readonly titleIcon?: string;
  readonly dataTestId?: string;
  readonly closeButtonDataTestId?: string;
  readonly unoptimizedTitleIcon?: boolean;
  // Id of a title element the caller renders inside `children`, used for the dialog's
  // `aria-labelledby` when the popup has no `title` prop of its own (e.g. payment-waiting-popup).
  readonly ariaLabelledBy?: string;
}
type PopupHandle = {
  close: () => void;
};
export type PopupComponentRef = ComponentRef<typeof PopupComponent>;
function PopupHeader(t0) {
  const $ = _c(16);
  const {
    title,
    titleId,
    closable,
    headerClass,
    popupType,
    titleIcon,
    unoptimizedTitleIcon,
    titleClassName,
    closeDialog,
    closeButton
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("popup");
  let t1;
  if ($[0] !== popupType || $[1] !== title || $[2] !== titleClassName || $[3] !== titleIcon || $[4] !== titleId || $[5] !== unoptimizedTitleIcon) {
    t1 = title && <div className={classNames(titleClassName[popupType] ?? "text-lg", styles.popup__title)}>{titleIcon && <ImageComponent className={styles["popup__title-icon"]} src={titleIcon} alt="" width={24} height={24} unoptimized={unoptimizedTitleIcon} />}<span id={titleId}>{title}</span></div>;
    $[0] = popupType;
    $[1] = title;
    $[2] = titleClassName;
    $[3] = titleIcon;
    $[4] = titleId;
    $[5] = unoptimizedTitleIcon;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== closable || $[8] !== closeButton || $[9] !== closeDialog || $[10] !== formatMessage) {
    t2 = closable && <><div className={styles["popup__remove-btn"]}><ButtonComponent buttonType={ButtonType.AS_TEXT} iconLeft={{
          src: "/assets/icons/remove-icon.svg",
          alt: formatMessage("removePopup"),
          width: 32,
          height: 32,
          unoptimized: true
        }} iconSize={IconSize.LARGE} onClick={closeDialog} aria-label={formatMessage("closePopup")} /></div><div className={styles["popup__close-btn"]}>{closeButton}</div></>;
    $[7] = closable;
    $[8] = closeButton;
    $[9] = closeDialog;
    $[10] = formatMessage;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== headerClass || $[13] !== t1 || $[14] !== t2) {
    t3 = <div className={headerClass}>{t1}{t2}</div>;
    $[12] = headerClass;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}
const PopupComponent = forwardRef<PopupHandle, Props>((t0, ref) => {
  const $ = _c(98);
  const {
    alertConfig,
    focusDialogOnOpen,
    children,
    closable: t1,
    footerContent,
    isOpen,
    onClosed,
    popupType: t2,
    popupContext: t3,
    dialogClassName,
    overlayClassName,
    headerClassName,
    title,
    titleIcon,
    dataTestId,
    closeButtonDataTestId,
    unoptimizedTitleIcon: t4,
    ariaLabelledBy
  } = t0;
  const closable = t1 === undefined ? true : t1;
  const popupType = t2 === undefined ? PopupType.DEFAULT : t2;
  const popupContext = t3 === undefined ? PopupContext.DEFAULT : t3;
  const unoptimizedTitleIcon = t4 === undefined ? true : t4;
  const [isClosing, setIsClosing] = useState(false);
  const {
    formatMessage
  } = useNamespacedIntl("popup");
  const titleId = useId();
  const dialogRef = useRef(null);
  const previousActiveElement = useRef(null);
  useBodyOverflowHidden(isOpen);
  let t5;
  let t6;
  if ($[0] !== focusDialogOnOpen || $[1] !== isOpen) {
    t5 = () => {
      if (isOpen && focusDialogOnOpen) {
        previousActiveElement.current = document.activeElement as HTMLElement;
        const timeoutId = setTimeout(() => {
          dialogRef.current?.focus();
        }, 0);
        return () => clearTimeout(timeoutId);
      } else {
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      }
    };
    t6 = [isOpen, focusDialogOnOpen];
    $[0] = focusDialogOnOpen;
    $[1] = isOpen;
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  useEffect(t5, t6);
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event => {
      if (dialogRef.current && event.key === "Tab") {
        const focusableElements = dialogRef.current.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])");
        if (focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
          if (event.shiftKey) {
            if (document.activeElement === dialogRef.current || document.activeElement === firstElement) {
              lastElement.focus();
              event.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              event.preventDefault();
            }
          }
        }
      }
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const handleTabKeyForFocusTrap = t7;
  let t8;
  let t9;
  if ($[5] !== focusDialogOnOpen || $[6] !== isOpen) {
    t8 = () => {
      if (isOpen && focusDialogOnOpen) {
        document.addEventListener("keydown", handleTabKeyForFocusTrap);
        return () => document.removeEventListener("keydown", handleTabKeyForFocusTrap);
      }
    };
    t9 = [isOpen, focusDialogOnOpen];
    $[5] = focusDialogOnOpen;
    $[6] = isOpen;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  useEffect(t8, t9);
  let t10;
  if ($[9] !== onClosed) {
    t10 = () => {
      setIsClosing(true);
      setTimeout(() => {
        onClosed();
        setIsClosing(false);
      }, 800);
    };
    $[9] = onClosed;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const closeDialog = t10;
  let t11;
  let t12;
  if ($[11] !== closable || $[12] !== closeDialog) {
    t11 = () => {
      const handleKeyDown = function handleKeyDown(event_0) {
        if (closable && event_0.key === "Escape") {
          closeDialog();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t12 = [closable, closeDialog];
    $[11] = closable;
    $[12] = closeDialog;
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[15] !== closeDialog) {
    t13 = () => ({
      close: closeDialog
    });
    t14 = [closeDialog];
    $[15] = closeDialog;
    $[16] = t13;
    $[17] = t14;
  } else {
    t13 = $[16];
    t14 = $[17];
  }
  useImperativeHandle(ref, t13, t14);
  const isHidden = isClosing || !isOpen;
  const t15 = popupType === PopupType.SUCCESS;
  const t16 = !isOpen && !isClosing;
  let t17;
  if ($[18] !== t15 || $[19] !== t16) {
    t17 = classNames(styles.popup, {
      [styles["popup--success"]]: t15,
      [styles["popup--hidden"]]: t16
    });
    $[18] = t15;
    $[19] = t16;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  const popupClass = t17;
  const t18 = !isHidden;
  const t19 = !isOpen && !isClosing;
  let t20;
  if ($[21] !== isClosing || $[22] !== overlayClassName || $[23] !== t18 || $[24] !== t19) {
    t20 = classNames(styles.popup__overlay, overlayClassName, {
      [styles["popup__overlay--show"]]: t18,
      [styles["popup__overlay--closing"]]: isClosing,
      [styles["popup__overlay--hidden"]]: t19
    });
    $[21] = isClosing;
    $[22] = overlayClassName;
    $[23] = t18;
    $[24] = t19;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  const overlayClass = t20;
  const t21 = !isHidden;
  const t22 = !isOpen && !isClosing;
  const t23 = popupType === PopupType.FULLSCREEN;
  const t24 = popupType === PopupType.FULLSCREEN_MOBILE;
  const t25 = popupType === PopupType.STOREFINDER;
  const t26 = popupType === PopupType.CENTER;
  const t27 = popupContext === PopupContext.WISHLIST;
  const t28 = popupContext === PopupContext.WITHOUT_TITLE;
  let t29;
  if ($[26] !== dialogClassName || $[27] !== isClosing || $[28] !== t21 || $[29] !== t22 || $[30] !== t23 || $[31] !== t24 || $[32] !== t25 || $[33] !== t26 || $[34] !== t27 || $[35] !== t28) {
    t29 = classNames(styles.popup__dialog, dialogClassName, {
      [styles["popup__dialog--show"]]: t21,
      [styles["popup__dialog--closing"]]: isClosing,
      [styles["popup__dialog--hidden"]]: t22,
      [styles["popup__dialog--full-screen"]]: t23,
      [styles["popup__dialog--full-screen-mobile"]]: t24,
      [styles["popup__dialog--storefinder"]]: t25,
      [styles["popup__dialog--center"]]: t26,
      [styles["popup__dialog--wishlist"]]: t27,
      [styles["popup__dialog--without-title"]]: t28
    });
    $[26] = dialogClassName;
    $[27] = isClosing;
    $[28] = t21;
    $[29] = t22;
    $[30] = t23;
    $[31] = t24;
    $[32] = t25;
    $[33] = t26;
    $[34] = t27;
    $[35] = t28;
    $[36] = t29;
  } else {
    t29 = $[36];
  }
  const dialogClass = t29;
  const t30 = popupType === PopupType.STOREFINDER;
  const t31 = popupType === PopupType.FULLSCREEN_MOBILE;
  let t32;
  if ($[37] !== t30 || $[38] !== t31) {
    t32 = classNames(styles.popup__content, {
      [styles["popup__content--storefinder"]]: t30,
      [styles["popup__content--full-screen-mobile"]]: t31
    });
    $[37] = t30;
    $[38] = t31;
    $[39] = t32;
  } else {
    t32 = $[39];
  }
  const contentClass = t32;
  const t33 = popupType === PopupType.FULLSCREEN || popupType === PopupType.FULLSCREEN_MOBILE;
  const t34 = popupType === PopupType.STOREFINDER;
  const t35 = popupType === PopupType.CENTER;
  let t36;
  if ($[40] !== headerClassName || $[41] !== t33 || $[42] !== t34 || $[43] !== t35) {
    t36 = classNames(styles.popup__header, headerClassName, {
      [styles["popup__header--full-screen"]]: t33,
      [styles["popup__header--storefinder"]]: t34,
      [styles["popup__header--center"]]: t35
    });
    $[40] = headerClassName;
    $[41] = t33;
    $[42] = t34;
    $[43] = t35;
    $[44] = t36;
  } else {
    t36 = $[44];
  }
  const headerClass = t36;
  let t37;
  if ($[45] !== formatMessage) {
    t37 = formatMessage("closePopup");
    $[45] = formatMessage;
    $[46] = t37;
  } else {
    t37 = $[46];
  }
  let t38;
  if ($[47] !== t37) {
    t38 = {
      src: "/assets/icons/close-icon.svg",
      alt: t37,
      width: 30,
      height: 30,
      unoptimized: true
    };
    $[47] = t37;
    $[48] = t38;
  } else {
    t38 = $[48];
  }
  let t39;
  if ($[49] !== formatMessage) {
    t39 = formatMessage("closePopup");
    $[49] = formatMessage;
    $[50] = t39;
  } else {
    t39 = $[50];
  }
  let t40;
  if ($[51] !== closeButtonDataTestId || $[52] !== closeDialog || $[53] !== t38 || $[54] !== t39) {
    t40 = <ButtonComponent buttonType={ButtonType.AS_TEXT} dataTestId={closeButtonDataTestId} iconLeft={t38} iconSize={IconSize.LARGE} onClick={closeDialog} aria-label={t39} />;
    $[51] = closeButtonDataTestId;
    $[52] = closeDialog;
    $[53] = t38;
    $[54] = t39;
    $[55] = t40;
  } else {
    t40 = $[55];
  }
  const closeButton = t40;
  let t41;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      [PopupType.FULLSCREEN]: "h2",
      [PopupType.LEGAL_NOTICE]: "h1"
    };
    $[56] = t41;
  } else {
    t41 = $[56];
  }
  const titleClassName = t41;
  const t42 = !isOpen;
  let t43;
  if ($[57] !== closable || $[58] !== closeDialog || $[59] !== formatMessage || $[60] !== overlayClass) {
    t43 = closable ? <button className={overlayClass} onClick={closeDialog}><span className="sr-only">{formatMessage("closePopup")}</span></button> : <div className={overlayClass} />;
    $[57] = closable;
    $[58] = closeDialog;
    $[59] = formatMessage;
    $[60] = overlayClass;
    $[61] = t43;
  } else {
    t43 = $[61];
  }
  const t44 = title ? titleId : ariaLabelledBy;
  const t45 = focusDialogOnOpen ? -1 : undefined;
  let t46;
  if ($[62] !== closable || $[63] !== closeButton || $[64] !== closeDialog || $[65] !== headerClass || $[66] !== popupType || $[67] !== title || $[68] !== titleIcon || $[69] !== titleId || $[70] !== unoptimizedTitleIcon) {
    t46 = (title || closable) && <PopupHeader title={title} titleId={titleId} closable={closable} headerClass={headerClass} popupType={popupType} titleIcon={titleIcon} unoptimizedTitleIcon={unoptimizedTitleIcon} titleClassName={titleClassName} closeDialog={closeDialog} closeButton={closeButton} />;
    $[62] = closable;
    $[63] = closeButton;
    $[64] = closeDialog;
    $[65] = headerClass;
    $[66] = popupType;
    $[67] = title;
    $[68] = titleIcon;
    $[69] = titleId;
    $[70] = unoptimizedTitleIcon;
    $[71] = t46;
  } else {
    t46 = $[71];
  }
  let t47;
  if ($[72] !== alertConfig || $[73] !== isHidden) {
    t47 = alertConfig && !isHidden && <div className={styles.popup__alert}><AlertComponent alertType={alertConfig.alertType} content={alertConfig.content} /></div>;
    $[72] = alertConfig;
    $[73] = isHidden;
    $[74] = t47;
  } else {
    t47 = $[74];
  }
  let t48;
  if ($[75] !== closeButton || $[76] !== popupType) {
    t48 = popupType === PopupType.STOREFINDER && <div className={styles["popup__close-btn-wrapper"]}>{closeButton}</div>;
    $[75] = closeButton;
    $[76] = popupType;
    $[77] = t48;
  } else {
    t48 = $[77];
  }
  let t49;
  if ($[78] !== children || $[79] !== contentClass || $[80] !== t48) {
    t49 = <div className={contentClass}>{t48}{children}</div>;
    $[78] = children;
    $[79] = contentClass;
    $[80] = t48;
    $[81] = t49;
  } else {
    t49 = $[81];
  }
  let t50;
  if ($[82] !== footerContent) {
    t50 = footerContent && <div className={styles.popup__footer}>{footerContent}</div>;
    $[82] = footerContent;
    $[83] = t50;
  } else {
    t50 = $[83];
  }
  let t51;
  if ($[84] !== dataTestId || $[85] !== dialogClass || $[86] !== t44 || $[87] !== t45 || $[88] !== t46 || $[89] !== t47 || $[90] !== t49 || $[91] !== t50) {
    t51 = <div ref={dialogRef} className={dialogClass} role="dialog" data-testid={dataTestId} aria-modal="true" aria-labelledby={t44} tabIndex={t45}>{t46}{t47}{t49}{t50}</div>;
    $[84] = dataTestId;
    $[85] = dialogClass;
    $[86] = t44;
    $[87] = t45;
    $[88] = t46;
    $[89] = t47;
    $[90] = t49;
    $[91] = t50;
    $[92] = t51;
  } else {
    t51 = $[92];
  }
  let t52;
  if ($[93] !== popupClass || $[94] !== t42 || $[95] !== t43 || $[96] !== t51) {
    t52 = <div className={popupClass} aria-hidden={t42}>{t43}{t51}</div>;
    $[93] = popupClass;
    $[94] = t42;
    $[95] = t43;
    $[96] = t51;
    $[97] = t52;
  } else {
    t52 = $[97];
  }
  return t52;
});
PopupComponent.displayName = 'PopupComponent';
export default PopupComponent;